import consola from 'consola'
/**
 * Константы для retry логики
 */
export const RETRY_CONSTANTS = {
  DEFAULT_MAX_RETRIES: 3,
  DEFAULT_BASE_DELAY_MS: 1000,
  QUICK_RETRY_MAX_RETRIES: 2,
  QUICK_RETRY_BASE_DELAY_MS: 500,
  RELIABLE_RETRY_MAX_RETRIES: 5,
  RELIABLE_RETRY_BASE_DELAY_MS: 2000,
} as const

/**
 * Утилита для retry запросов с экспоненциальной задержкой
 * @param requestFn - функция запроса для выполнения
 * @param maxRetries - максимальное количество попыток
 * @param baseDelay - базовая задержка в миллисекундах
 * @returns результат выполнения функции
 */
export async function retryRequest<T>(
  requestFn: () => Promise<T>,
  maxRetries: number = RETRY_CONSTANTS.DEFAULT_MAX_RETRIES,
  baseDelay: number = RETRY_CONSTANTS.DEFAULT_BASE_DELAY_MS,
): Promise<T> {
  let lastError: any
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await requestFn()
    } catch (error: any) {
      lastError = error
      consola.warn(`Attempt ${attempt + 1} failed:`, error.message)
      
      // Если это последняя попытка, выбрасываем ошибку
      if (attempt === maxRetries - 1) {
        throw lastError
      }
      
      // Экспоненциальная задержка: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  
  throw lastError
}

/**
 * Выполняет быстрый retry с уменьшенными параметрами
 * @param requestFn - функция запроса
 * @returns результат выполнения функции
 */
export function quickRetry<T>(requestFn: () => Promise<T>): Promise<T> {
  return retryRequest(
    requestFn,
    RETRY_CONSTANTS.QUICK_RETRY_MAX_RETRIES,
    RETRY_CONSTANTS.QUICK_RETRY_BASE_DELAY_MS,
  )
}

/**
 * Выполняет надежный retry с увеличенными параметрами
 * @param requestFn - функция запроса
 * @returns результат выполнения функции
 */
export function reliableRetry<T>(requestFn: () => Promise<T>): Promise<T> {
  return retryRequest(
    requestFn,
    RETRY_CONSTANTS.RELIABLE_RETRY_MAX_RETRIES,
    RETRY_CONSTANTS.RELIABLE_RETRY_BASE_DELAY_MS,
  )
}

/**
 * Создает функцию с предустановленными параметрами retry
 * @param maxRetries - максимальное количество попыток
 * @param baseDelay - базовая задержка
 * @returns функция для выполнения retry запросов
 */
export function createRetryFunction(maxRetries: number, baseDelay: number) {
  return function<T> (requestFn: () => Promise<T>): Promise<T> {
    return retryRequest(requestFn, maxRetries, baseDelay)
  }
}

/**
 * Логирует попытку выполнения запроса
 * @param attempt - номер попытки
 * @param maxRetries - максимальное количество попыток
 * @param operation - описание операции
 */
export function logRetryAttempt(attempt: number, maxRetries: number, operation: string): void {
  consola.debug(`🔄 Retry attempt ${attempt}/${maxRetries} for operation: ${operation}`)
}

/**
 * Логирует успешное выполнение после retry
 * @param attempt - номер успешной попытки
 * @param operation - описание операции
 */
export function logRetrySuccess(attempt: number, operation: string): void {
  consola.debug(`✅ Retry succeeded on attempt ${attempt} for operation: ${operation}`)
}

/**
 * Логирует неудачное выполнение всех попыток retry
 * @param maxRetries - максимальное количество попыток
 * @param operation - описание операции
 * @param error - последняя ошибка
 */
export function logRetryFailure(maxRetries: number, operation: string, error: any): void {
  consola.error(`❌ All ${maxRetries} retry attempts failed for operation: ${operation}`, error)
}